import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppError } from "@/jenesys-types/errors";
import { EmailForwardingSender } from "@/jenesys-types/jenesys-entities/config";

export interface EmailForwardingSendersState {
  senders: EmailForwardingSender[];
  isLoading: boolean;
  error?: AppError;
  isAddingSender: boolean;
  isUpdatingSender: boolean;
  isDeletingSender: boolean;
  isUploadingCSV: boolean;
}

const initialState: EmailForwardingSendersState = {
  senders: [],
  isLoading: false,
  error: undefined,
  isAddingSender: false,
  isUpdatingSender: false,
  isDeletingSender: false,
  isUploadingCSV: false
};

export const emailForwardingSendersSlice = createSlice({
  name: "emailForwardingSenders",
  initialState,
  reducers: {
    loadSenders: {
      reducer: (state) => {
        state.isLoading = true;
        state.error = undefined;
      },
      prepare: (companyId: string) => ({
        payload: { companyId }
      })
    },
    loadSendersSuccess: (
      state,
      action: PayloadAction<EmailForwardingSender[]>
    ) => {
      state.senders = action.payload;
      state.isLoading = false;
    },
    loadSendersFailure: (state, action: PayloadAction<AppError>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    addSender: {
      reducer: (state) => {
        state.isAddingSender = true;
        state.error = undefined;
      },
      prepare: (
        companyId: string,
        data: Omit<EmailForwardingSender, "id" | "date_added">,
        onSuccess?: () => void
      ) => ({
        payload: { companyId, data, onSuccess }
      })
    },
    addSenderSuccess: (state, action: PayloadAction<EmailForwardingSender>) => {
      state.senders.push(action.payload);
      state.isAddingSender = false;
    },
    addSenderFailure: (state, action: PayloadAction<AppError>) => {
      state.error = action.payload;
      state.isAddingSender = false;
    },
    updateSender: {
      reducer: (state) => {
        state.isUpdatingSender = true;
        state.error = undefined;
      },
      prepare: (
        companyId: string,
        id: string,
        data: Partial<EmailForwardingSender>,
        onSuccess?: () => void
      ) => ({
        payload: { companyId, id, data, onSuccess }
      })
    },
    updateSenderSuccess: (
      state,
      action: PayloadAction<EmailForwardingSender>
    ) => {
      const index = state.senders.findIndex(
        (s) => s.email === action.payload.email
      );
      if (index !== -1) {
        state.senders[index] = action.payload;
      }
      state.isUpdatingSender = false;
    },
    updateSenderFailure: (state, action: PayloadAction<AppError>) => {
      state.error = action.payload;
      state.isUpdatingSender = false;
    },
    deleteSender: {
      reducer: (state) => {
        state.isDeletingSender = true;
        state.error = undefined;
      },
      prepare: (companyId: string, email: string, onSuccess?: () => void) => ({
        payload: { companyId, email, onSuccess }
      })
    },
    deleteSenderSuccess: (state, action: PayloadAction<string>) => {
      state.senders = state.senders.filter((s) => s.email !== action.payload);
      state.isDeletingSender = false;
    },
    deleteSenderFailure: (state, action: PayloadAction<AppError>) => {
      state.error = action.payload;
      state.isDeletingSender = false;
    },
    uploadCSV: {
      reducer: (state) => {
        state.isUploadingCSV = true;
        state.error = undefined;
      },
      prepare: (companyId: string, file: File, onSuccess?: () => void) => ({
        payload: { companyId, file, onSuccess }
      })
    },
    uploadCSVSuccess: (
      state,
      action: PayloadAction<EmailForwardingSender[]>
    ) => {
      state.senders = [...state.senders, ...action.payload];
      state.isUploadingCSV = false;
    },
    uploadCSVFailure: (state, action: PayloadAction<AppError>) => {
      state.error = action.payload;
      state.isUploadingCSV = false;
    }
  }
});

export const {
  loadSenders,
  loadSendersSuccess,
  loadSendersFailure,
  addSender,
  addSenderSuccess,
  addSenderFailure,
  updateSender,
  updateSenderSuccess,
  updateSenderFailure,
  deleteSender,
  deleteSenderSuccess,
  deleteSenderFailure,
  uploadCSV,
  uploadCSVSuccess,
  uploadCSVFailure
} = emailForwardingSendersSlice.actions;

export default emailForwardingSendersSlice.reducer;
